import CheckRounded from '@mui/icons-material/CheckRounded';
import CloudDoneOutlined from '@mui/icons-material/CloudDoneOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import NorthEastRounded from '@mui/icons-material/NorthEastRounded';
import PointOfSaleRounded from '@mui/icons-material/PointOfSaleRounded';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import WhatsApp from '@mui/icons-material/WhatsApp';
import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { Wordmark } from '../components/Brand';
import { PoweredBy } from '../components/PoweredBy';
import { fonts } from '../design/theme';
import { BrandMark, ModeToggle } from '../layout/AppShell';

/** The PackPilot greens used by the sign-in screens (before any shop's own colour applies). */
const AUTH_GREEN = {
  ink: '#03130F',
  deep: '#04241F',
  base: '#05473E',
  bright: '#0A6B5C',
  mint: '#6EE7B7',
} as const;

const fadeUp = (delayMs: number) => ({
  animation: `pp-fade-up 520ms cubic-bezier(0.2,0,0,1) ${String(delayMs)}ms both`,
  '@keyframes pp-fade-up': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'none' },
  },
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

/**
 * Sign-in, sign-up and invitation screens: the form on a calm canvas, beside a dark emerald panel
 * that shows the product. The panel hides on small screens.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
  back,
  panel = 'signin',
}: {
  title: string;
  subtitle: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** A way back (e.g. "Back to sign in"), above the title. */
  back?: ReactNode;
  /** What the product panel says. */
  panel?: 'signin' | 'signup';
}) {
  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0,1fr)', md: '1fr 1fr', xl: '1fr 1.15fr' },
        bgcolor: 'background.default',
      }}
    >
      <Box
        sx={(t) => ({
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          // The whole column scrolls as one beside the hero, so on a short laptop every option
          // (sign in, create a shop, the desktop-app card, the footer) stays reachable in order
          // instead of being clipped or pushed off-screen.
          minHeight: '100dvh',
          height: { md: '100dvh' },
          overflowY: { md: 'auto' },
          px: { xs: 2.5, sm: 5, lg: 8 },
          py: { xs: 2.5, sm: 3.5 },
          background: `radial-gradient(900px 420px at 0% 0%, ${alpha(AUTH_GREEN.bright, 0.07)}, transparent 70%)`,
          ...t.applyStyles('dark', {
            background: `radial-gradient(900px 420px at 0% 0%, ${alpha(AUTH_GREEN.mint, 0.06)}, transparent 70%)`,
          }),
        })}
      >
        <Stack direction="row" sx={{ alignItems: 'center' }}>
          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
            <BrandMark size={36} />
            <Wordmark />
          </Stack>
          <Box sx={{ ml: 'auto' }}>
            <ModeToggle />
          </Box>
        </Stack>

        <Box
          sx={{
            // Grows to centre the form when there's room; keeps its natural height and lets the
            // column scroll when the viewport is too short, so nothing overlaps the footer.
            flex: '1 0 auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            py: { xs: 3, md: 4 },
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 420, mx: 'auto', ...fadeUp(0) }}>
            {back && <Box sx={{ mb: 3, ml: -1 }}>{back}</Box>}
            <Typography
              component="h1"
              sx={{
                fontFamily: fonts.display,
                fontWeight: 750,
                fontSize: { xs: '1.9rem', sm: '2.25rem' },
                lineHeight: 1.12,
                letterSpacing: '-0.035em',
                mb: 1.25,
              }}
            >
              {title}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ mb: { xs: 2.5, sm: 4 }, fontSize: '0.975rem' }}
            >
              {subtitle}
            </Typography>
            <Box sx={authFieldsSx}>{children}</Box>
            {footer && <Box sx={{ mt: 3 }}>{footer}</Box>}
          </Box>
        </Box>

        <PoweredBy />
      </Box>

      <ProductPanel kind={panel} />
    </Box>
  );
}

/** Taller, softer inputs with a brand focus ring, for every field inside the auth form. */
const authFieldsSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: 'background.paper',
    transition: 'box-shadow 160ms, border-color 160ms',
    '&.Mui-focused': {
      boxShadow: (t: { palette: { primary: { main: string } } }) =>
        `0 0 0 4px ${alpha(t.palette.primary.main, 0.14)}`,
    },
  },
  '& .MuiOutlinedInput-input': { py: '14px' },
} as const;

// ---------------------------------------------------------------- the product panel

const glass = {
  bgcolor: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.12)',
  backdropFilter: 'blur(14px) saturate(140%)',
  borderRadius: '16px',
} as const;

const BARS = [38, 52, 44, 61, 57, 70, 64, 78, 72, 86, 81, 94];

const INVOICES = [
  { no: 'INV-2026-01042', who: 'Ahmed Builders', amount: 'Rs 12,500', paid: true },
  { no: 'INV-2026-01041', who: 'City Hardware', amount: 'Rs 8,940', paid: false },
  { no: 'POS-0318', who: 'Walk-in customer', amount: 'Rs 2,150', paid: true },
];

const COPY = {
  signin: {
    tag: 'Sales · Stock · Accounts · POS',
    head: ['Your whole shop,', 'in one calm place.'],
    sub: 'Sell at the counter, track every rupee owed and know what is on the shelf — on the web and on your desktop, even offline.',
  },
  signup: {
    tag: '30-day free trial · every feature',
    head: ['Open your shop', 'in under a minute.'],
    sub: 'Start empty or bring a backup with you. Invite your staff, print your first invoice and sell from the desktop app the same day.',
  },
} as const;

const FEATURES = [
  { icon: PointOfSaleRounded, label: 'Point of Sale' },
  { icon: Inventory2Outlined, label: 'Stock & recipes' },
  { icon: ReceiptLongOutlined, label: 'Invoices & ledger' },
  { icon: WhatsApp, label: 'WhatsApp & email' },
];

const PERKS = [
  'Every feature open during the trial',
  'Desktop app keeps selling when the internet drops',
  'Restore an existing PackPilot backup',
];

function ProductPanel({ kind }: { kind: 'signin' | 'signup' }) {
  const copy = COPY[kind];
  return (
    <Box sx={{ display: { xs: 'none', md: 'block' }, p: 1.5, minHeight: '100dvh' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 12,
          height: 'calc(100dvh - 24px)',
          minHeight: 600,
          borderRadius: '28px',
          overflow: 'hidden',
          isolation: 'isolate',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          p: { md: 5, lg: 6 },
          background: [
            `radial-gradient(700px 480px at 100% 0%, ${alpha('#34D399', 0.28)}, transparent 65%)`,
            `radial-gradient(640px 520px at 0% 100%, ${alpha('#14B8A6', 0.22)}, transparent 65%)`,
            `linear-gradient(160deg, ${AUTH_GREEN.base} 0%, ${AUTH_GREEN.deep} 52%, ${AUTH_GREEN.ink} 100%)`,
          ].join(', '),
        }}
      >
        {/* Fine grid, fading out towards the edges */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: -1,
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
            maskImage: 'radial-gradient(ellipse at 60% 35%, black 20%, transparent 72%)',
          }}
        />

        <Box sx={{ maxWidth: 520, ...fadeUp(80) }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.6,
              mb: 3,
              borderRadius: 999,
              fontSize: 12.5,
              fontWeight: 600,
              letterSpacing: 0.1,
              bgcolor: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.16)',
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: AUTH_GREEN.mint,
                boxShadow: `0 0 0 4px ${alpha(AUTH_GREEN.mint, 0.2)}`,
              }}
            />
            {copy.tag}
          </Box>
          <Typography
            component="p"
            sx={{
              fontFamily: fonts.display,
              fontWeight: 750,
              fontSize: { md: '2.4rem', lg: '3rem' },
              lineHeight: 1.06,
              letterSpacing: '-0.04em',
            }}
          >
            {copy.head[0]}
            <br />
            <Box
              component="span"
              sx={{
                background: `linear-gradient(90deg, ${AUTH_GREEN.mint}, #D1FAE5 55%, #5EEAD4)`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              {copy.head[1]}
            </Box>
          </Typography>
          <Typography
            sx={{ mt: 2, color: 'rgba(255,255,255,0.72)', fontSize: '1.02rem', maxWidth: 460 }}
          >
            {copy.sub}
          </Typography>
        </Box>

        <Box sx={{ mt: 3.5, ...fadeUp(160) }}>
          {kind === 'signin' ? (
            <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
              {FEATURES.map(({ icon: Icon, label }) => (
                <Stack
                  key={label}
                  direction="row"
                  spacing={0.75}
                  sx={{
                    alignItems: 'center',
                    px: 1.25,
                    py: 0.75,
                    borderRadius: '10px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.85)',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <Icon sx={{ fontSize: 16, color: AUTH_GREEN.mint }} />
                  <span>{label}</span>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Stack spacing={1.25}>
              {PERKS.map((p) => (
                <Stack key={p} direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: alpha(AUTH_GREEN.mint, 0.16),
                      color: AUTH_GREEN.mint,
                    }}
                  >
                    <CheckRounded sx={{ fontSize: 15 }} />
                  </Box>
                  <Typography sx={{ fontSize: 14.5, color: 'rgba(255,255,255,0.85)' }}>
                    {p}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
        <Box sx={{ flex: 1, position: 'relative', mt: 6, minHeight: 280, ...fadeUp(260) }}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: { md: 'calc(100% + 40px)', lg: 'calc(100% + 48px)' },
            }}
          >
            <DashboardWindow />
          </Box>
          <Toast
            sx={{ position: 'absolute', left: { md: -24, lg: -30 }, top: { md: 236, lg: 250 } }}
            icon={<CheckRounded sx={{ fontSize: 18 }} />}
            tone="#12B76A"
            title="Payment received"
            text="Ahmed Builders · Rs 12,500"
          />
          <Toast
            sx={{ position: 'absolute', right: { md: 12, lg: 24 }, top: -26 }}
            icon={<CloudDoneOutlined sx={{ fontSize: 18 }} />}
            tone="#2E90FA"
            title="Desktop synced"
            text="3 offline sales sent"
          />
        </Box>
      </Box>
    </Box>
  );
}

/** A believable app window: KPI tiles, a sales chart and the latest documents. */
function DashboardWindow() {
  return (
    <Box
      aria-hidden
      sx={{
        ...glass,
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 40px 80px -30px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04) inset',
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        sx={{
          alignItems: 'center',
          px: 2,
          py: 1.25,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        {['#F97066', '#FDB022', '#47CD89'].map((c) => (
          <Box
            key={c}
            sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c, opacity: 0.85 }}
          />
        ))}
        <Typography
          sx={{ ml: 1.5, fontSize: 12, color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}
        >
          Dashboard
        </Typography>
      </Stack>

      <Box sx={{ p: 2.25 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 1.25 }}>
          <Kpi label="Sales today" value="Rs 248,500" delta="18.2%" />
          <Kpi label="Customers owe" value="Rs 86,200" />
          <Kpi label="Gross profit" value="Rs 61,940" delta="9.4%" />
        </Box>

        <Box
          sx={{
            mt: 1.25,
            p: 1.75,
            borderRadius: '12px',
            bgcolor: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.07)',
          }}
        >
          <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 1.25 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 650, color: 'rgba(255,255,255,0.85)' }}>
              Sales · last 12 days
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Rs</Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'flex-end', height: 86 }}>
            {BARS.map((h, i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  height: `${String(h)}%`,
                  borderRadius: '5px 5px 2px 2px',
                  background:
                    i === BARS.length - 1
                      ? `linear-gradient(180deg, ${AUTH_GREEN.mint}, #10B981)`
                      : 'linear-gradient(180deg, rgba(110,231,183,0.55), rgba(16,185,129,0.18))',
                }}
              />
            ))}
          </Stack>
        </Box>

        <Stack spacing={0} sx={{ mt: 1.25 }}>
          {INVOICES.map((r) => (
            <Stack
              key={r.no}
              direction="row"
              spacing={1.5}
              sx={{
                alignItems: 'center',
                py: 1,
                px: 0.5,
                borderTop: '1px solid rgba(255,255,255,0.06)',
                '&:first-of-type': { borderTop: 0 },
              }}
            >
              <Typography
                sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: 600, width: 112 }}
              >
                {r.no}
              </Typography>
              <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.88)', flex: 1 }} noWrap>
                {r.who}
              </Typography>
              <Typography
                sx={{ fontSize: 13, fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}
              >
                {r.amount}
              </Typography>
              <Box
                sx={{
                  px: 0.9,
                  py: 0.2,
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  color: r.paid ? '#A6F4C5' : '#FEDF89',
                  bgcolor: r.paid ? 'rgba(18,183,106,0.16)' : 'rgba(247,144,9,0.16)',
                }}
              >
                {r.paid ? 'Paid' : 'Due'}
              </Box>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

function Kpi({ label, value, delta }: { label: string; value: string; delta?: string }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: '12px',
        bgcolor: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.07)',
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,0.55)', fontWeight: 600 }} noWrap>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: fonts.display,
          fontSize: { md: 15, lg: 18 },
          fontWeight: 750,
          letterSpacing: '-0.02em',
          mt: 0.25,
        }}
        noWrap
      >
        {value}
      </Typography>
      {delta && (
        <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center', mt: 0.25 }}>
          <NorthEastRounded sx={{ fontSize: 12, color: AUTH_GREEN.mint }} />
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: AUTH_GREEN.mint }}>
            {delta}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

function Toast({
  icon,
  tone,
  title,
  text,
  sx,
}: {
  icon: ReactNode;
  tone: string;
  title: string;
  text: string;
  sx: object;
}) {
  return (
    <Stack
      aria-hidden
      direction="row"
      spacing={1.25}
      sx={{
        alignItems: 'center',
        p: 1.25,
        pr: 2,
        borderRadius: '14px',
        bgcolor: 'rgba(255,255,255,0.94)',
        color: '#101828',
        boxShadow: '0 24px 48px -16px rgba(0,0,0,0.55)',
        ...sx,
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: '10px',
          display: 'grid',
          placeItems: 'center',
          color: '#fff',
          bgcolor: tone,
          boxShadow: `0 6px 14px -4px ${alpha(tone, 0.6)}`,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>{title}</Typography>
        <Typography sx={{ fontSize: 12, color: '#667085' }}>{text}</Typography>
      </Box>
    </Stack>
  );
}
